import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaClock,
  FaFileAlt,
  FaImages,
  FaHome,
  FaComments,
  FaDatabase,
  FaUsers,
  FaWhatsapp,
  FaCreditCard,
  FaClipboardList,
  FaBox,
  FaChartBar,
  FaWarehouse,
  FaBoxOpen,
  FaTags,
  FaRegCalendarAlt,
  FaUserTie,
} from "react-icons/fa";
import { FaInstagram } from "react-icons/fa";
import { CloseOutlined } from "@ant-design/icons";
import { MdMiscellaneousServices, MdPolicy } from "react-icons/md";
import { Layout, Menu, Drawer } from "antd";
import logo from "../../assets/img/dashboard/webp/logo_BN.webp";
import "./Sidebar.css";

const { Sider } = Layout;

interface CustomSidenavProps {
  signOut: () => void;
  user_data: any;
  isMobile: boolean;
  onToggleMobileSider: () => void;
  isMobileSiderVisible: boolean;
}

const CustomSidenav: React.FC<CustomSidenavProps> = ({
  user_data,
  isMobile,
  onToggleMobileSider,
  isMobileSiderVisible,
}) => {
  const [loadingUserData, setLoadingUserData] = useState(true);
  const location = useLocation();
  const [selectedKey, setSelectedKey] = useState<string>(location.pathname);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    setSelectedKey(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (user_data) {
      setLoadingUserData(false);
    }
  }, [user_data]);

  const isAdmin = user_data?.["custom:group_user"] === "Administrador";
  const isEcommerce = user_data?.["custom:establishment"] === "e-commerce";

  const handleMenuClick = () => {
    if (isMobile) {
      onToggleMobileSider();
    }
  };

  const allowedNames = ["Centro de gestión de riesgo SPCI", "LaborHealth IPS"];

  const showReservations = [
    "restaurant",
    "nightclub",
    "bar",
    "real-estate",
    "medical-office",
    "legal-office",
  ].includes(user_data?.["custom:establishment"]);

  const modules = [
    {
      key: "/dashboard/general",
      label: "General",
      icon: <FaHome />,
      children: [
        {
          key: "/dashboard/general-data",
          label: (
            <Link to="/dashboard/general-data" onClick={handleMenuClick}>
              Datos
            </Link>
          ),
          icon: <FaFileAlt />,
        },
        {
          key: "/dashboard/general-services",
          label: (
            <Link to="/dashboard/general-services" onClick={handleMenuClick}>
              Servicios
            </Link>
          ),
          icon: <MdMiscellaneousServices />,
        },
        {
          key: "/dashboard/schedule",
          label: (
            <Link to="/dashboard/schedule" onClick={handleMenuClick}>
              Horarios
            </Link>
          ),
          icon: <FaClock />,
        },
        {
          key: "/dashboard/policy-data",
          label: (
            <Link to="/dashboard/policy-data" onClick={handleMenuClick}>
              Políticas
            </Link>
          ),
          icon: <MdPolicy />,
        },
        {
          key: "/dashboard/multimedia",
          label: (
            <Link to="/dashboard/multimedia" onClick={handleMenuClick}>
              Multimedia
            </Link>
          ),
          icon: <FaImages />,
        },
        {
          key: "/dashboard/inventory",
          label: "Inventario",
          icon: <FaWarehouse />,
          children: [
            {
              key: "/dashboard/menu-categories",
              label: (
                <Link to="/dashboard/menu-categories" onClick={handleMenuClick}>
                  Categorías
                </Link>
              ),
              icon: <FaTags />,
            },
            {
              key: "/dashboard/products",
              label: (
                <Link to="/dashboard/products" onClick={handleMenuClick}>
                  Productos
                </Link>
              ),
              icon: <FaBoxOpen />,
            },
          ],
        },
        {
          key: "/dashboard/faqs",
          label: (
            <Link to="/dashboard/faqs" onClick={handleMenuClick}>
              FAQs
            </Link>
          ),
          icon: <FaFileAlt />,
        },
      ],
    },
    {
      key: "/dashboard/payment-data",
      label: (
        <Link to="/dashboard/payment-data" onClick={handleMenuClick}>
          Pagos
        </Link>
      ),
      icon: <FaCreditCard />,
    },
    ...(isAdmin
      ? [
          {
            key: "/dashboard/plans-data",
            label: (
              <Link to="/dashboard/plans-data" onClick={handleMenuClick}>
                Planes
              </Link>
            ),
            icon: <FaClipboardList />,
          },
          {
            key: "/dashboard/packages-data",
            label: (
              <Link to="/dashboard/packages-data" onClick={handleMenuClick}>
                Paquetes
              </Link>
            ),
            icon: <FaBox />,
          },
          {
            key: "/dashboard/agents",
            label: "Agentes",
            icon: <FaUsers />,
            children: [
              {
                key: "/dashboard/agents-data",
                label: (
                  <Link to="/dashboard/agents-data" onClick={handleMenuClick}>
                    Agentes Personalizados
                  </Link>
                ),
                icon: <FaUsers />,
              },
              {
                key: "/dashboard/agents-company-data",
                label: (
                  <Link to="/dashboard/agents-company-data" onClick={handleMenuClick}>
                    Datos de Agentes
                  </Link>
                ),
                icon: <FaUserTie />,
              },
            ],
          },
        ]
      : []),
    ...(showReservations || allowedNames.includes(user_data.family_name)
      ? [
          {
            key: "/dashboard/reservation-request",
            label: (
              <Link
                to="/dashboard/reservation-request"
                onClick={handleMenuClick}
              >
                Reservas
              </Link>
            ),
            icon: <FaRegCalendarAlt />,
          },
        ]
      : []),
    {
      key: "/dashboard/chats",
      label: "Chats",
      icon: <FaComments />,
      children: [
        {
          key: "/dashboard/normal-chat",
          label: (
            <Link to="/dashboard/normal-chat" onClick={handleMenuClick}>
              Chat Interno
            </Link>
          ),
          icon: <FaUsers />,
        },
        {
          key: "/dashboard/whatsapp-chat",
          label: (
            <Link to="/dashboard/whatsapp-chat" onClick={handleMenuClick}>
              Gestionar WhatsApp
            </Link>
          ),
          icon: <FaWhatsapp />,
        },
        {
          key: "/dashboard/instagram-chat",
          label: (
            <Link to="/dashboard/instagram-chat" onClick={handleMenuClick}>
              Gestionar Instagram
            </Link>
          ),
          icon: <FaInstagram />,
        },
      ],
    },
    {
      key: "/dashboard/chat-data",
      label: (
        <Link to="/dashboard/chat-data" onClick={handleMenuClick}>
          Historial de Chats
        </Link>
      ),
      icon: <FaDatabase />,
    },
    {
      key: "/dashboard/analytics",
      label: (
        <Link to="/dashboard/analytics" onClick={handleMenuClick}>
          Analítica
        </Link>
      ),
      icon: <FaChartBar />,
    },
  ];

  if (!user_data) {
    return (
      <div style={{ textAlign: "center", marginTop: "100px", color: "red" }}>
        Error: Datos de usuario no disponibles.
      </div>
    );
  }

  const siderStyle: React.CSSProperties = {
    overflow: "auto",
    height: "100vh",
    insetInlineStart: 0,
    top: 0,
    bottom: 0,
    scrollbarWidth: "thin",
    paddingTop: 20,
  };

  const drawerStyle: React.CSSProperties = {
    background: "#001529",
  };

  const SiderContent = (
    <>
      <Link
        to={"/dashboard"}
        style={{ width: "100%" }}
        onClick={handleMenuClick}
      >
        <img
          style={{ width: "100%" }}
          src={logo}
          alt="Logo horizontal de Blox"
          loading="lazy"
        />
      </Link>
      <Menu
        theme="dark"
        mode="inline"
        items={modules}
        style={{ marginTop: 20 }}
        selectedKeys={[selectedKey]}
      />
    </>
  );

  return isMobile ? (
    <Drawer
      open={isMobileSiderVisible}
      onClose={onToggleMobileSider}
      placement="left"
      width={250}
      style={drawerStyle}
      styles={{ body: { padding: 0 } }}
      closeIcon={<CloseOutlined style={{ color: "#fff", fontSize: "18px" }} />}
    >
      {SiderContent}
    </Drawer>
  ) : (
    <Sider
      style={siderStyle}
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
      width={250}
    >
      {SiderContent}
    </Sider>
  );
};

export default CustomSidenav;
